import { useEffect, useRef, useState } from 'react'
import { NavLink, useLocation } from 'react-router-dom'
import styled from 'styled-components'

import AssessmentRoundedIcon from '@mui/icons-material/AssessmentRounded'
import EventNoteRoundedIcon from '@mui/icons-material/EventNoteRounded'
import HistoryRoundedIcon from '@mui/icons-material/HistoryRounded'
import HomeRoundedIcon from '@mui/icons-material/HomeRounded'
import MoreHorizRoundedIcon from '@mui/icons-material/MoreHorizRounded'
import PersonRoundedIcon from '@mui/icons-material/PersonRounded'
import TrackChangesRoundedIcon from '@mui/icons-material/TrackChangesRounded'
import { ReactComponent as AkiveLogo } from '../assets/images/akive_logo.svg'
import { ROUTES } from '../constants/routes'

const primaryNavigation = [
  { to: ROUTES.HOME, label: '홈', mobileLabel: '홈', icon: HomeRoundedIcon, end: true },
  { to: ROUTES.PLANS, label: '학습 계획', mobileLabel: '계획', icon: EventNoteRoundedIcon },
  { to: ROUTES.DAILY_LOGS, label: '하루 기록', mobileLabel: '기록', icon: HistoryRoundedIcon },
  { to: ROUTES.REPORTS, label: '학습 리포트', mobileLabel: '리포트', icon: AssessmentRoundedIcon },
]

const secondaryNavigation = [
  { to: ROUTES.ROUTINE_GOALS, label: '학습 루틴 목표', icon: TrackChangesRoundedIcon },
]

const mobileSecondaryNavigation = [
  { to: ROUTES.MY_PAGE, label: '마이페이지', icon: PersonRoundedIcon },
  ...secondaryNavigation,
]

function NavigationItem({ item, compact = false, onClick }) {
  const Icon = item.icon

  return (
    <NavItem to={item.to} end={item.end} $compact={compact} onClick={onClick}>
      <Icon aria-hidden="true" />
      <span>{compact ? item.mobileLabel || item.label : item.label}</span>
    </NavItem>
  )
}

function Sidebar({
  nickname,
  aim,
  profileImageUrl,
  desktopHidden = false,
  mobileHidden = false,
}) {
  const [moreOpen, setMoreOpen] = useState(false)
  const firstMoreItemRef = useRef(null)
  const location = useLocation()

  useEffect(() => {
    setMoreOpen(false)
  }, [location.pathname])

  useEffect(() => {
    if (!moreOpen) return undefined

    firstMoreItemRef.current?.focus()

    const handleEscape = (event) => {
      if (event.key === 'Escape') setMoreOpen(false)
    }

    window.addEventListener('keydown', handleEscape)
    return () => window.removeEventListener('keydown', handleEscape)
  }, [moreOpen])

  return (
    <>
      <DesktopSidebar id="desktop-sidebar" $hidden={desktopHidden} aria-label="주요 메뉴">
        <DesktopSidebarInner $hidden={desktopHidden} aria-hidden={desktopHidden}>
          <BrandLink to={ROUTES.HOME} aria-label="akive 홈으로 이동">
            <SidebarLogo aria-hidden="true" />
          </BrandLink>

          <DesktopNav>
            <NavGroup aria-label="학습 메뉴">
              {primaryNavigation.map((item) => <NavigationItem key={item.to} item={item} />)}
            </NavGroup>
            <NavDivider />
            <NavGroup aria-label="내 정보 메뉴">
              {secondaryNavigation.map((item) => <NavigationItem key={item.to} item={item} />)}
            </NavGroup>
          </DesktopNav>

          <ProfileLink to={ROUTES.MY_PAGE} aria-label="마이페이지로 이동">
            <ProfileImage src={profileImageUrl} alt="" />
            <ProfileText>
              <strong>{nickname || '학습자'}</strong>
              <span>{aim || '오늘도 한 걸음씩'}</span>
            </ProfileText>
          </ProfileLink>
        </DesktopSidebarInner>
      </DesktopSidebar>

      <MobileNav
        $hidden={mobileHidden && !moreOpen}
        aria-label="모바일 주요 메뉴"
      >
        {primaryNavigation.map((item) => <NavigationItem key={item.to} item={item} compact />)}
        <MobileMoreButton
          type="button"
          $active={moreOpen || mobileSecondaryNavigation.some(({ to }) => location.pathname.startsWith(to))}
          aria-label="더보기"
          aria-expanded={moreOpen}
          aria-controls="mobile-more-menu"
          onClick={() => setMoreOpen((open) => !open)}
        >
          <MoreHorizRoundedIcon aria-hidden="true" />
          <span>더보기</span>
        </MobileMoreButton>
      </MobileNav>

      {moreOpen && (
        <>
          <MobileMenuBackdrop aria-label="더보기 메뉴 닫기" onClick={() => setMoreOpen(false)} />
          <MobileMoreMenu id="mobile-more-menu" aria-label="더보기 메뉴">
            <MobileMenuHeading>더보기</MobileMenuHeading>
            {mobileSecondaryNavigation.map((item, index) => {
              const Icon = item.icon
              return (
                <MobileMenuLink key={item.to} to={item.to} ref={index === 0 ? firstMoreItemRef : undefined}>
                  <Icon aria-hidden="true" />
                  <span>{item.label}</span>
                </MobileMenuLink>
              )
            })}
          </MobileMoreMenu>
        </>
      )}
    </>
  )
}

const DesktopSidebar = styled.aside`
  position: relative;
  z-index: 20;
  display: flex;
  width: ${({ $hidden }) => ($hidden ? '0' : '224px')};
  min-width: ${({ $hidden }) => ($hidden ? '0' : '224px')};
  height: 100dvh;
  flex-direction: column;
  padding: ${({ $hidden }) => ($hidden ? '24px 0 18px' : '24px 16px 18px')};
  color: #FFFFFF;
  background: ${({ theme }) => theme.colors.primaryFill};
  overflow: hidden;
  transition: width 180ms ease, min-width 180ms ease, padding 180ms ease;

  @media (max-width: ${({ theme }) => theme.breakpoint.mobile}) { display: none; }
`

const DesktopSidebarInner = styled.div`
  display: flex;
  width: 192px;
  min-height: 0;
  flex: 1;
  flex-direction: column;
  opacity: ${({ $hidden }) => ($hidden ? 0 : 1)};
  visibility: ${({ $hidden }) => ($hidden ? 'hidden' : 'visible')};
  pointer-events: ${({ $hidden }) => ($hidden ? 'none' : 'auto')};
  transition: opacity 100ms ease, visibility 100ms ease;
`

const BrandLink = styled(NavLink)`
  display: inline-flex;
  align-items: center;
  align-self: flex-start;
  margin: 4px 12px 38px;
  color: #FFFFFF;
`

const SidebarLogo = styled(AkiveLogo)`
  display: block;
  width: 98px;
  height: 30px;
  shape-rendering: geometricPrecision;

  path, rect { shape-rendering: geometricPrecision; }
`

const DesktopNav = styled.nav`
  min-height: 0;
  flex: 1;
`

const NavGroup = styled.div`
  display: grid;
  gap: 6px;
`

const NavDivider = styled.hr`
  height: 1px;
  margin: 18px 10px;
  border: 0;
  background: rgba(255, 255, 255, 0.14);
`

const NavItem = styled(NavLink)`
  display: flex;
  min-width: 0;
  min-height: ${({ $compact }) => ($compact ? '58px' : '46px')};
  align-items: center;
  justify-content: ${({ $compact }) => ($compact ? 'center' : 'flex-start')};
  gap: ${({ $compact }) => ($compact ? '3px' : '12px')};
  padding: ${({ $compact }) => ($compact ? '7px 2px 6px' : '0 13px')};
  color: ${({ $compact }) => ($compact ? 'rgba(255, 255, 255, 0.68)' : 'rgba(255, 255, 255, 0.72)')};
  border-radius: ${({ theme }) => theme.radius.small};
  font-size: ${({ $compact }) => ($compact ? '0.69rem' : '0.93rem')};
  font-weight: 620;
  transition: color 150ms ease, background 150ms ease;

  svg {
    width: ${({ $compact }) => ($compact ? '22px' : '21px')};
    height: ${({ $compact }) => ($compact ? '22px' : '21px')};
  }

  ${({ $compact }) => $compact && 'flex-direction: column;'}

  &:hover {
    color: #FFFFFF;
    background: rgba(255, 255, 255, 0.08);
  }

  &.active {
    color: #FFFFFF;
    background: rgba(255, 255, 255, 0.14);
  }
`

const ProfileLink = styled(NavLink)`
  display: grid;
  grid-template-columns: 38px minmax(0, 1fr);
  align-items: center;
  gap: 10px;
  padding: 12px 10px;
  color: #FFFFFF;
  border: 1px solid rgba(255, 255, 255, 0.13);
  border-radius: ${({ theme }) => theme.radius.medium};
  background: rgba(255, 255, 255, 0.06);

  &:hover { background: rgba(255, 255, 255, 0.1); }
`

const ProfileImage = styled.img`
  width: 38px;
  height: 38px;
  border: 2px solid rgba(255, 255, 255, 0.32);
  border-radius: 50%;
  object-fit: cover;
`

const ProfileText = styled.span`
  min-width: 0;

  strong, span {
    display: block;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }

  strong { font-size: 0.84rem; }
  span {
    margin-top: 2px;
    color: rgba(255, 255, 255, 0.62);
    font-size: 0.7rem;
  }
`

const MobileNav = styled.nav`
  position: fixed;
  z-index: 70;
  right: 0;
  bottom: 0;
  left: 0;
  display: none;
  height: calc(66px + env(safe-area-inset-bottom));
  grid-template-columns: repeat(5, minmax(0, 1fr));
  padding: 4px 6px env(safe-area-inset-bottom);
  border-top: 1px solid rgba(255, 255, 255, 0.14);
  border-radius: 16px 16px 0 0;
  background: ${({ theme }) => theme.colors.primaryFill};
  box-shadow: 0 -2px 8px rgba(17, 27, 64, 0.08);
  pointer-events: ${({ $hidden }) => ($hidden ? 'none' : 'auto')};
  transform: translateY(${({ $hidden }) => ($hidden ? 'calc(100% + 1px)' : '0')});
  transition: transform 180ms ease;

  @media (max-width: ${({ theme }) => theme.breakpoint.mobile}) { display: grid; }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`

const MobileMoreButton = styled.button`
  display: flex;
  min-width: 0;
  min-height: 58px;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 3px;
  padding: 7px 2px 6px;
  color: ${({ $active }) => ($active ? '#FFFFFF' : 'rgba(255, 255, 255, 0.68)')};
  border: 0;
  border-radius: ${({ theme }) => theme.radius.small};
  background: ${({ $active }) => ($active ? 'rgba(255, 255, 255, 0.14)' : 'transparent')};
  font-size: 0.69rem;
  font-weight: 620;

  svg { width: 22px; height: 22px; }

  &:hover {
    color: #FFFFFF;
    background: rgba(255, 255, 255, 0.08);
  }
`

const MobileMenuBackdrop = styled.button`
  position: fixed;
  z-index: 71;
  inset: 0 0 calc(66px + env(safe-area-inset-bottom));
  width: 100%;
  border: 0;
  background: rgba(15, 23, 42, 0.24);

  @media (min-width: 721px) { display: none; }
`

const MobileMoreMenu = styled.nav`
  position: fixed;
  z-index: 72;
  right: 12px;
  bottom: calc(76px + env(safe-area-inset-bottom));
  display: grid;
  width: min(280px, calc(100vw - 24px));
  gap: 4px;
  padding: 12px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.large};
  background: ${({ theme }) => theme.colors.surface};
  box-shadow: ${({ theme }) => theme.shadow.medium};

  @media (min-width: 721px) { display: none; }
`

const MobileMenuHeading = styled.strong`
  padding: 6px 8px 8px;
  font-size: 0.82rem;
`

const MobileMenuLink = styled(NavLink)`
  display: flex;
  min-height: 46px;
  align-items: center;
  gap: 11px;
  padding: 0 10px;
  color: ${({ theme }) => theme.colors.text};
  border-radius: ${({ theme }) => theme.radius.small};
  font-size: 0.9rem;
  font-weight: 620;

  svg {
    color: ${({ theme }) => theme.colors.textMuted};
    font-size: 1.25rem;
  }

  &:hover, &.active {
    color: ${({ theme }) => theme.colors.primary};
    background: ${({ theme }) => theme.colors.primarySoft};
  }
`

export default Sidebar
